/**
 * Prolean Subscriptions — unified selection, price display, and cart bridge (v3).
 *
 * Single source of truth for the Shopify Subscriptions purchase options:
 *  - Marks exactly one option selected via a `.prolean-selected` class (the app
 *    can leave One-time's radio permanently :checked, so the class is used
 *    instead of the radio state).
 *  - Injects each option's price + savings badge and mirrors the selected
 *    option's price into Dawn's main price.
 *  - Keeps a hidden `selling_plan` input on the add-to-cart form in sync so the
 *    correct purchase option reaches the cart.
 * Replaces the older split core/price/sync scripts (a fresh filename also side-
 * steps stale-asset CDN caching).
 */
(() => {
  'use strict';

  if (window.__proleanSubsV3) return;
  window.__proleanSubsV3 = true;

  const OPTIONS = '.shopify_subscriptions_app_block_purchase_options';
  const LABEL = '.shopify_subscriptions_app_block_label';
  const CONTAINER = '.subscriptions_app_embed_block';
  const FORM = 'form[action*="/cart/add"]';
  const observers = new WeakMap();

  const clean = (v) => String(v ?? '').replace(/\s+/g, ' ').trim();
  const money = (s) => {
    const n = parseFloat(String(s || '').replace(/[^0-9.,]/g, '').replace(/,/g, ''));
    return Number.isFinite(n) ? n : 0;
  };
  const sectionFor = (n) => n?.closest?.('product-info') || n?.closest?.('.shopify-section') || null;
  const sectionKey = (s) => s?.dataset?.section || s?.id || s?.getAttribute?.('data-section') || '';
  const optLabels = (o) => [...o.querySelectorAll(':scope > ' + LABEL)];
  const primaryRadio = (l) => l.querySelector(':scope > label input[type="radio"]');

  function sig(label) {
    if (!label) return null;
    if (label.querySelector('input[data-radio-type="one_time_purchase"]')) return 'one_time';
    const el = label.querySelector('[data-selling-plan-id]');
    const id = (String(el?.getAttribute('data-selling-plan-id') || el?.value || '').match(/\d+/) || [''])[0];
    return id ? 'plan:' + id : null;
  }
  function planId(label) {
    const el = label.querySelector('[data-selling-plan-id]');
    return (String(el?.getAttribute('data-selling-plan-id') || el?.value || '').match(/\d+/) || [''])[0];
  }

  // Resolve the selected option (stable across app re-renders via signature) and
  // mark it with the authoritative class.
  function resolve(section) {
    const options = section.querySelector(OPTIONS);
    if (!options) return { options: null, opts: [], sel: null };
    const opts = optLabels(options);
    let sel = section._proleanSig ? opts.find((l) => sig(l) === section._proleanSig) : null;
    if (!sel) {
      sel =
        opts.find((l) => l.querySelector('input[data-radio-type="selling_plan_group"]:checked')) ||
        opts.find((l) => primaryRadio(l)?.checked) ||
        opts[0] ||
        null;
      section._proleanSig = sig(sel);
    }
    opts.forEach((l) => l.classList.toggle('prolean-selected', l === sel));
    return { options, opts, sel };
  }

  function mainPrice(section) {
    const k = sectionKey(section);
    return (
      (k && section.querySelector(`#price-${CSS.escape(k)} .price`)) ||
      section.querySelector('[id^="price-"] .price') ||
      section.querySelector('.product__info-container .price') ||
      null
    );
  }
  function optionPrice(label) {
    if (!label) return null;
    let s = label.querySelector('script');
    if (!s) {
      const sib = label.nextElementSibling;
      if (sib && sib.tagName === 'SCRIPT') s = sib;
    }
    if (!s) return null;
    try {
      const d = JSON.parse(s.textContent);
      const p = clean(d.price);
      if (!p) return null;
      const c = clean(d.comparedAtPrice);
      return { price: p, compareAt: c && c !== p ? c : '' };
    } catch (e) {
      return null;
    }
  }
  const savings = (p, c) => {
    const a = money(p);
    const b = money(c);
    return a && b && b > a ? Math.round(((b - a) / b) * 100) : 0;
  };
  const setText = (n, v) => {
    if (n && v != null && n.textContent !== v) n.textContent = v;
  };

  function decorate(opts) {
    opts.forEach((label) => {
      const info = optionPrice(label);
      if (!info) return;
      const host = label.querySelector(':scope > label') || label;
      let box = host.querySelector(':scope > .prolean-po-price');
      if (!box) {
        box = document.createElement('span');
        box.className = 'prolean-po-price';
        host.appendChild(box);
      }
      const pct = savings(info.price, info.compareAt);
      const badge = pct ? `<span class="prolean-po-save">Save ${pct}%</span>` : '';
      const was = info.compareAt ? `<s class="prolean-po-was">${info.compareAt}</s>` : '';
      const html = `${badge}<span class="prolean-po-amount">${was}<span class="prolean-po-now">${info.price}</span></span>`;
      if (box.dataset.render !== html) {
        box.innerHTML = html;
        box.dataset.render = html;
      }
    });
  }
  function reflect(section, sel) {
    const price = mainPrice(section);
    const info = optionPrice(sel);
    if (!price || !info) return;
    const regular = price.querySelector('.price__regular .price-item--regular');
    const was = price.querySelector('.price__sale .price-item--regular');
    const sale = price.querySelector('.price__sale .price-item--sale, .price__sale .price-item--last');
    const onSale = Boolean(info.compareAt);
    if (onSale) {
      setText(was, info.compareAt);
      setText(sale, info.price);
      setText(regular, info.compareAt);
    } else {
      setText(regular, info.price);
      setText(was, info.price);
      setText(sale, info.price);
    }
    price.classList.toggle('price--on-sale', onSale);
    price.classList.toggle('prolean-sub-price', onSale);
  }

  function productForm(section) {
    const forms = [...section.querySelectorAll(FORM)].filter(
      (f) => f.querySelector('input[name="id"], select[name="id"]') && !f.querySelector('input[name="form_type"][value="product_installment"]'),
    );
    return forms.find((f) => f.closest('product-form')) || forms[0] || null;
  }
  function canonicalInput(form) {
    let i = form.querySelector('input[data-prolean-selling-plan]');
    if (!i) {
      i = document.createElement('input');
      i.type = 'hidden';
      i.name = 'selling_plan';
      i.dataset.proleanSellingPlan = 'true';
      form.appendChild(i);
    }
    return i;
  }
  function syncCart(section, sel) {
    const form = productForm(section);
    if (!form) return;
    const input = canonicalInput(form);
    const oneTime = !sel || sel.querySelector('input[data-radio-type="one_time_purchase"]');
    const plan = oneTime ? '' : planId(sel);
    if (plan) {
      input.value = plan;
      input.disabled = false;
    } else {
      input.value = '';
      input.disabled = true;
    }
  }

  function update(section) {
    const obs = observers.get(section);
    if (obs) obs.disconnect();
    try {
      const { options, opts, sel } = resolve(section);
      if (options) {
        decorate(opts);
        reflect(section, sel);
        syncCart(section, sel);
      }
    } finally {
      if (obs) {
        const t = section.querySelector(CONTAINER) || section.querySelector(OPTIONS) || section;
        obs.observe(t, { childList: true, subtree: true });
      }
    }
  }
  function schedule(section) {
    if (!section) return;
    requestAnimationFrame(() => update(section));
    [50, 200, 600].forEach((d) => setTimeout(() => update(section), d));
  }

  function optionLabel(e) {
    const t = e.target;
    if (!(t instanceof Element)) return null;
    const radio = t.matches('input[type="radio"]') ? t : t.closest('label')?.querySelector('input[type="radio"]');
    if (!radio || !radio.closest(OPTIONS)) return null;
    return radio.closest(LABEL);
  }
  function handle(e) {
    const l = optionLabel(e);
    if (!l) return;
    const s = sectionFor(l);
    if (!s) return;
    s._proleanSig = sig(l);
    schedule(s);
  }
  document.addEventListener('change', handle, true);
  document.addEventListener('click', handle, true);
  document.addEventListener(
    'submit',
    (e) => {
      const f = e.target;
      if (f instanceof HTMLFormElement && f.matches(FORM)) {
        const s = sectionFor(f);
        if (s) update(s);
      }
    },
    true,
  );

  function observe(section) {
    if (observers.has(section)) return;
    const obs = new MutationObserver(() => update(section));
    observers.set(section, obs);
    const t = section.querySelector(CONTAINER) || section.querySelector(OPTIONS) || section;
    obs.observe(t, { childList: true, subtree: true });
  }
  function variantChanges() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined' || !PUB_SUB_EVENTS.variantChange) return;
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      const id = String(data?.sectionId || '');
      const s = document.querySelector(`product-info[data-section="${CSS.escape(id)}"]`) || document.querySelector('product-info');
      if (s) schedule(s);
    });
  }
  let pending = false;
  function init(root = document) {
    root.querySelectorAll?.(OPTIONS).forEach((o) => {
      const s = sectionFor(o);
      if (s) {
        observe(s);
        update(s);
      }
    });
  }
  function initSoon() {
    if (pending) return;
    pending = true;
    requestAnimationFrame(() => {
      pending = false;
      init(document);
    });
  }
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  const page = new MutationObserver((records) => {
    if (records.some((r) => r.addedNodes.length)) initSoon();
  });
  function start() {
    init(document);
    variantChanges();
    page.observe(document.body, { childList: true, subtree: true });
    // Re-run through the app's hydration churn so the default selection sticks.
    [100, 400, 900, 1600].forEach((ms) => setTimeout(() => init(document), ms));
  }
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', start, { once: true }) : start();
})();
